import { useEffect, useRef, useCallback } from "react";
import { useActivityTracker } from "./useActivityTracker";

interface UseSessionTimeoutOptions {
    timeoutMs: number;
    throttleMs?: number;
    onTimeout: () => void;
    enabled: boolean;
}

/**
 * Manages an inactivity-based session timeout.
 * Resets the timer whenever the user has activity (via useActivityTracker).
 * Persists last-activity timestamp to localStorage so expiry survives page
 * reloads AND is shared across same-origin tabs: when the local timer fires,
 * we re-check `lastActivity` and reschedule for the remaining slice if any
 * tab (or an active recording heartbeat) has written a more recent timestamp.
 */
export function useSessionTimeout({ timeoutMs, throttleMs = 5000, onTimeout, enabled }: UseSessionTimeoutOptions) {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const scheduleCheck = useCallback((delay: number) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            const stored = localStorage.getItem('lastActivity');
            const last = stored ? parseInt(stored, 10) : 0;
            const remaining = last ? timeoutMs - (Date.now() - last) : 0;
            if (remaining > 0) {
                scheduleCheck(remaining);
            } else {
                onTimeout();
            }
        }, delay);
    }, [timeoutMs, onTimeout]);

    const resetTimer = useCallback(() => {
        if (!enabled) return;
        localStorage.setItem('lastActivity', Date.now().toString());
        scheduleCheck(timeoutMs);
    }, [enabled, timeoutMs, scheduleCheck]);

    useActivityTracker(resetTimer, throttleMs);

    useEffect(() => {
        if (!enabled) return;
        resetTimer();
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [enabled, resetTimer]);
}
